/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useActionable } from '../hooks/UNSAFE_useActionable';
import { useCallback } from 'preact/hooks';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { multiVariantStyles } from './themes/ChipStyles.css';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { mergeProps } from '../utils/UNSAFE_mergeProps';

type SelectedChangeDetail = { previousValue?: boolean; value?: boolean };

type Props = {
  /**
   * Specifies if the chip component is selected.
   */
  isSelected?: boolean;
  /**
   * Disables the component.
   */
  isDisabled?: boolean;
  /**
   * Specifies some screen reader text.
   */
  'aria-label'?: string;
  children: string;
  /**
   * Triggered when a chip is clickable, whether by keyboard, mouse, or touch events.
   */
  onToggle?: (detail: SelectedChangeDetail) => void;
  //
};

export function Chip({
  isSelected,
  isDisabled,
  'aria-label': accessibleLabel,
  children,
  onToggle
}: Props) {
  const handleAction = useCallback(() => {
    onToggle?.({ previousValue: isSelected, value: !isSelected });
  }, [isSelected, onToggle]);
  const { actionableProps } = useActionable(handleAction);
  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle();

  const mergedProps = mergeProps(actionableProps, interactionProps);

  const classes = multiVariantStyles({
    disabled: isDisabled ? 'isDisabled' : 'notDisabled',
    hover: applyHoverStyle && !isDisabled && !isSelected ? 'isHover' : 'notHover',
    pseudoHover:
      applyPseudoHoverStyle && !isDisabled && !isSelected ? 'isPseudoHover' : 'notPseudoHover',
    active: isSelected || applyActiveStyle ? 'isActive' : 'notActive'
  });

  const { isTabbable } = useTabbableMode();

  return isDisabled ? (
    <div class={classes}>{children}</div>
  ) : (
    <div
      {...mergedProps}
      class={classes}
      tabIndex={isTabbable ? 0 : -1}
      aria-label={accessibleLabel}
      role="switch"
      aria-checked={isSelected ? 'true' : 'false'}>
      {children}
    </div>
  );
}
